import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ModalAd from './ModalAd'
import { useRouter } from 'next/router'
import { inject, observer } from 'mobx-react'

const SEEN_MODALS_KEY = 'chalice_seen_modals'
const CURRENT_PAGE_MODAL_KEY = 'chalice_current_page_modal'

const ModalAdsContainer = inject('store')(observer(({ modals = [], store }) => {
  const router = useRouter()
  const currentPath = router.asPath
  const [seenModals, setSeenModals] = useState([])
  const [currentPageModal, setCurrentPageModal] = useState(null)

  // Load seen modals and current page modal from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedSeen = localStorage.getItem(SEEN_MODALS_KEY)
      if (storedSeen) {
        setSeenModals(JSON.parse(storedSeen))
      }

      const storedCurrentModal = localStorage.getItem(CURRENT_PAGE_MODAL_KEY)
      if (storedCurrentModal) {
        setCurrentPageModal(JSON.parse(storedCurrentModal))
      }
    }
  }, [])

  // Save seen modals to localStorage when they change
  useEffect(() => {
    if (typeof window !== 'undefined' && seenModals.length > 0) {
      localStorage.setItem(SEEN_MODALS_KEY, JSON.stringify(seenModals))
    }
  }, [seenModals])

  // Clear current page modal when path changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CURRENT_PAGE_MODAL_KEY)
      setCurrentPageModal(null)
    }
  }, [currentPath])

  // Filter modals based on modal_behavior pattern and seen status
  const filteredModals = React.useMemo(() => {
    if (!modals || modals.length < 1) return []

    return modals.filter(modal => {
      const pattern = modal.modal_behavior
      if (!pattern) return false

      if (pattern === '*') return true

      const regexPattern = pattern
        .replace(/\*/g, '.*') // Convert * to .*
        .replace(/^\//, '') // Remove leading slash from pattern
        .replace(/\//g, '\\/') // Escape remaining forward slashes
      const regex = new RegExp(`^${regexPattern}$`)

      // Remove leading slash from current path for comparison
      const pathToTest = currentPath.replace(/^\//, '')

      const matchesPattern = regex.test(pathToTest)
      if (!matchesPattern) return false

      // For authenticated users, the backend handles seen modals
      if (store.user) return true

      // For unauthenticated users, check localStorage
      return !seenModals.includes(modal.id)
    })
  }, [modals, currentPath, store.user, seenModals])

  // Set current page modal if we have filtered modals and no current modal
  useEffect(() => {
    if (filteredModals.length > 0 && !currentPageModal && typeof window !== 'undefined') {
      const modalToShow = filteredModals[0]
      localStorage.setItem(CURRENT_PAGE_MODAL_KEY, JSON.stringify(modalToShow))
      setCurrentPageModal(modalToShow)
    }
  }, [filteredModals, currentPageModal])

  // Handle modal interaction (dismiss/click) for unauthenticated users
  const handleModalInteraction = (modalId, action) => {
    if (!store.user) {
      setSeenModals(prev => {
        if (!prev.includes(modalId)) {
          return [...prev, modalId]
        }
        return prev
      })
    }
  }

  if (!currentPageModal) return null

  return (
    <Container>
      <ModalAd
        key={currentPageModal.id}
        modal={currentPageModal}
        onInteraction={handleModalInteraction}
      />
    </Container>
  )
}))

ModalAdsContainer.propTypes = {
  modals: PropTypes.array
}

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1000;
`

export default ModalAdsContainer
